import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AiService } from 'src/app/business-service/ai/ai.service';
import { InventoryService } from 'src/app/business-service/inventory/inventory.service';
import { ItineraryService } from 'src/app/business-service/quote/itinerary.service';
import { MEAL_PLANS } from 'src/app/modules/shared/constants/api-constants';
import { GlobalService } from 'src/app/modules/shared/services/global/global.service';
import { LovDataService } from 'src/app/modules/shared/services/lov-data/lov-data.service';
import { ResponseNotificationService } from 'src/app/modules/shared/services/response-notification/response-notification.service';
import { InputValidationsService } from 'src/app/modules/shared/services/validations/input-validations.service';

@Component({
  selector: 'app-route-craft',
  templateUrl: './route-craft.component.html',
  styleUrls: ['./route-craft.component.scss'],
})
export class RouteCraftComponent implements OnInit {

  @Input() leadId: any;
  @Input() itineraryId: any;
  @Output() viewChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() storeFormData: EventEmitter<any> = new EventEmitter();

  @Output() updateQuote: EventEmitter<void> = new EventEmitter();
  @Output() updateInventoryItinerary: EventEmitter<void> = new EventEmitter();

  @Input() itineraryDetails: any;
  @Input() storedFormData: any;
  @Input() editInventory: boolean = false;
  @Input() editQuote: boolean = false;

  @Input() serviceBasedSections: any;

  noOfNights: number = 0;

  radioValue: string = 'FreeText';

  routeCraftForm!: FormGroup;

  showClearBtn: boolean = false;
  role: any;
  hideAiSection: any;

  meals: any = [];
  MEAL_PLAN = MEAL_PLANS;

  constructor(
    private _fb: FormBuilder,
    private _itineraryService: ItineraryService,
    private _inventoryService: InventoryService,
    private _aiService: AiService,
    private _responseNotificationService: ResponseNotificationService,
    private _globalService: GlobalService,
    private _validationService: InputValidationsService,
    private _lovDataService: LovDataService
  ) { }

  ngOnInit() {
    this.noOfNights = this.itineraryDetails?.totalDays + 1;
    this.role = localStorage.getItem("Role");

    this.intilizeFreeTextForm();

    this._lovDataService.getLovData("Meals").subscribe((data) => {
      this.meals = data;
    });

    if (this.storedFormData) {
      this.patchFreeTextForm(this.storedFormData);
      if(this.storedFormData.dataInvalid){
        this.routeCraftForm.markAllAsTouched();
        this.routeCraftForm.markAsDirty();
      }
    }


    this._globalService.roles$.subscribe((roles) => {
      if (roles) {
        const aiModule = roles.modules.find((module: any) => module.name === "Ai");
        if (!aiModule) {
          this.hideAiSection = true;
        }
        else
        {
          this.hideAiSection = false;
        }
      }
    })
  }

  ngOnChanges(){ 
    this.ngOnInit();
  }

  async clearAllRouteFormData() {
    if (await this._globalService.confirmActionPopup("Confirm to clear to data!")) {
      this.intilizeFreeTextForm();
    }
    this.routeCraftForm.markAsTouched();
  }

  intilizeFreeTextForm() {
    this.routeCraftForm = this._fb.group({
      sectionNote: [''],
      days: this._fb.array(this.createFreeTextDays()),
      acknowledgement: [false, Validators.requiredTrue]
    });
    this.routeCraftForm.markAsPristine();

    this.routeCraftForm.valueChanges.subscribe((value) => {
      this.routeCraftForm.markAsTouched();
    })

    // when days changes, the acknowledgement should be set to false
    this.routeCraftForm.get('days')?.valueChanges.subscribe(() => {
      this.routeCraftForm.get('acknowledgement')?.setValue(false);
    });
  }
  createFreeTextDays() {
    let arr = [];
    for (let i = 0; i < this.noOfNights; i++) {
      arr.push(this._fb.group({
        title: ['', Validators.required],
        description: ['', this._validationService.textEditorRequired()],
        mealPlan: [null]
      }));
    }

    return arr;
  }

  get freeTextDays() {
    return this.routeCraftForm.get('days') as FormArray;
  }

  changeRadioValue(val: string) {
    this.radioValue = val;
  }

  patchFreeTextForm(storedFormData: any) {
    const freeTextDaysArray = this.routeCraftForm.get('days') as FormArray;
    storedFormData.data.forEach((day: any, index: number) => {
      if (freeTextDaysArray.length <= index) {
        // Skip this iteration if the condition is met
        return;
      }

      freeTextDaysArray.at(index).patchValue({
        title: day.day,
        description: day.activities,
        mealPlan: day.mealPlan
      });
    });

    this.routeCraftForm.patchValue({
      sectionNote: storedFormData.note,
      acknowledgement: storedFormData.acknowledgement
    })
  }

  async onSubmit(): Promise<boolean> {
    if (!this.routeCraftForm.valid) {
      this.routeCraftForm.markAllAsTouched();
      this._globalService.scrollToFirstInvalidControl();
      if(!this.routeCraftForm.get('acknowledgement')?.value){
        this._globalService.scrollToBottomOfContainer('.itinerary-model-container .itinerary-model-content-main .itinerary-model-content-inner .itinerary-model-content')
      }
      this._globalService.customAlert("Fill all required fields to save", "Invalid Form");
      return false;
    }

    const selectedFormData = this.routeCraftForm.value;
    const formData = await this.serializeFormData(selectedFormData);

    if (this.leadId) {
      return this.handleLeadSpecificRouteCraft(formData);
    }

    return this.handleDefaultRouteCraft(formData);
  }

  private async handleLeadSpecificRouteCraft(formData: any): Promise<boolean> {
    if (this.editQuote) {
      this.finalizeFormSubmission(formData);
      this.updateQuote.emit();
      return true;
    }

    try {
      const data = await this._itineraryService.addRouteCraft(formData).toPromise();
      this._responseNotificationService.showMessage("Route Craft Added!");
      this.finalizeFormSubmission(data);
      return true;
    } catch (error) {
      console.error("Error adding route craft to lead", error);
      return false;
    }
  }

  private async handleDefaultRouteCraft(formData: any): Promise<boolean> {
    if (this.editInventory) {
      this.finalizeFormSubmission(formData);
      this.updateInventoryItinerary.emit();
      return true;
    }

    try {
      const data = await this._inventoryService.addInventoryRouteCraft(formData).toPromise();
      this._responseNotificationService.showMessage("Route Craft Added!");
      this.finalizeFormSubmission(data);
      return true;
    } catch (error) {
      console.error("Error adding inventory route craft", error);
      return false;
    }
  }

  private finalizeFormSubmission(data: any): void {
    this.routeCraftForm.markAsPristine();
    data.dataSaved = true;
    this.storeFormData.emit(data);
  }


  private async serializeFormData(formData: any) {
    const serializedData = {
      leadId: this.leadId,
      itineraryId: this.itineraryId,
      type: "RouteCraftFreeText",
      data: formData.days.map((day: any) => ({
        day: day.title,
        activities: day.description,
        mealPlan: day.mealPlan,
      })),
      organizationId: this.storedFormData?.organizationId,
      note: formData.sectionNote,
      acknowledgement: formData.acknowledgement,
      id: this.storedFormData ? this.storedFormData.id : null,
      createdBy: this.storedFormData ? this.storedFormData.createdBy : null,
      ...(this.storedFormData && { createDate: this.storedFormData.createDate }),
      dataSaved: true,
      dataInvalid: false
    }
    return serializedData;
  }

  generateAiRouteAndPatch() {
    let allDestinations = []

    for (let i = 0; i < this.itineraryDetails.destination.length; i++) {
      let destination = this.itineraryDetails.destination[i];
      let tempDest = {
        location: destination.location,
        days: destination.days,
        nights: destination.days
      };
      // If it's the last destination, add an extra day to the 'days' property
      if (i === this.itineraryDetails.destination.length - 1) {
        tempDest.days += 1;
      }
      allDestinations.push(tempDest);
    }
    this._aiService.getAiGeneratedRouteCraftJson(allDestinations).subscribe((data) => {
      this.patchAiRouteCraft(data)
    }, (error) => {
      this._responseNotificationService.showMessage("RouteCraft generation failed. Please check the input data.", true);
    })
  }

  patchAiRouteCraft(aiData: any) {
    this.freeTextDays.clear()

    let totalNights = 0;
    for (let data of aiData) {
      if (!data.routeItineraryDayWise) {
        this._responseNotificationService.showMessage("RouteCraft generation failed. Please check the input data.", true);
        this.ngOnInit();
        return;
      }
      let keys = Object.keys(data.routeItineraryDayWise.days);
      for (let key of keys) {
        if (key === "note" || key === "notes") {
          continue;
        }
        let dayPlan = data.routeItineraryDayWise.days[key];
        let title: string = dayPlan.header;
        let description: string = dayPlan.content;

        // Replace all occurrences of '*' with an empty string
        title = title.replace(/\*/g, "");
        description = description.replace(/\*/g, "");

        totalNights++;
        let group = this._fb.group({
          title: [title, Validators.required],
          description: [description, this._validationService.textEditorRequired()],
          mealPlan: [null]
        })

        this.freeTextDays.push(group)
      }
    }

    this.routeCraftForm.markAsDirty();
    this.routeCraftForm.markAsTouched();

    if (totalNights !== this.noOfNights) {
      this._responseNotificationService.showMessage("RouteCraft generation failed. Please check the input data.", true);
      this.intilizeFreeTextForm();
    }


  }

  chooseFileForRouteCraft(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/* , .pdf , .doc, .docx, .txt';
    input.onchange = (event: any) => this.onRouteFileSelect(event);
    input.click();
  }

  onRouteFileSelect(event: any): void {
    const file = event.target.files[0];
    if (!file || file.size > 2 * 1024 * 1024) {
      this._globalService.customAlert('Image must be less than 2MB', 'Invalid File Size');
      return;
    }

    const formData = new FormData();
    formData.append('document', file);
    formData.append('days', this.noOfNights.toString());

    this._itineraryService.uploadRouteCraftDocument(formData).subscribe({
      next: (res) => {
        this.patchRouteCraftDocument(res);
      }, 
      error: (err) => {
        this._responseNotificationService.showMessage("Something went wrong, please try again.", true);
      }
    })
  }

  patchRouteCraftDocument(data:any){
    this.freeTextDays.clear()

    let totalNights = 0;
    if (!data.routeItineraryDayWise) {
      this._responseNotificationService.showMessage("RouteCraft generation failed. Please check the input data.", true);
      this.ngOnInit();
      return;
    }
    let keys = Object.keys(data.routeItineraryDayWise.days);
    for (let key of keys) {
      if (key === "note" || key === "notes") {
        continue;
      }
      let dayPlan = data.routeItineraryDayWise.days[key];
      let title: string = dayPlan.header;
      let description: string = dayPlan.content;

      // Replace all occurrences of '*' with an empty string
      title = title.replace(/\*/g, "");
      description = description.replace(/\*/g, "");

      totalNights++;
      let group = this._fb.group({
        title: [title, Validators.required],
        description: [description.replace('\n', '<br>'), this._validationService.textEditorRequired()],
        mealPlan: [null]
      })

      this.freeTextDays.push(group)
    }

    this.routeCraftForm.markAsDirty();
    this.routeCraftForm.markAsTouched();

    if (totalNights !== this.noOfNights) {
      this._responseNotificationService.showMessage("No of days do not match with Trip Details information", true);
      this.intilizeFreeTextForm();
    }else{
      this._responseNotificationService.showMessage("RouteCraft document uploaded successfully!");
    }


  }

  async resetForm() {
    if (await this._globalService.confirmActionPopup("Confirm to reset data", "Confirm", "Confirm")) {
      this.ngOnInit();
    }
  }

  goPrevious() {
    this.viewChange.emit(true);
  }

  goNext() {
    this.viewChange.emit(false);
  }

  async saveAndNext(){
    if(await this.onSubmit()){
      this.goNext()
    }
  }

  formDataModified(): boolean {
    return (this.routeCraftForm?.dirty || this.routeCraftForm?.invalid) && this.routeCraftForm?.touched;
  }

  getDateByTripIndex(index: number): Date {
      const date = new Date(this.itineraryDetails.startDate);
      date.setDate(date.getDate() + index);
      return date;
    }
}
